// screens/SimulaScreen.tsx — G2: Simula mode — durable internal state machine.
//
// State 1: ModePicker  — three cards (Cieca / Guidata / Difficile)
// State 2: Running     — 30 questions with timer, progress bar, V/F answers
// Finalizing / not-saved are explicit persistence states before PostMortem.
// Review walks through wrong answers with Prof explanations (2026-04-22).
//
// Conversational-first IA (2026-05-06): Simula Esame is a standalone section
// at `/simula`. (V29 S8: the W4.6 embedded/Allena hosting is retired.)

import type { JSX } from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { ActionChips } from '../components/ActionChips';
import { ConfirmModal } from '../components/ConfirmModal';
import { ChatThread } from '../components/ChatThread';
import { QCard } from '../components/QCard';
import { VeroFalsoPills } from '../components/VeroFalsoPills';
import { useTokens } from '../state/theme';
import { probabilityBand, TABBAR_H } from '../tokens';
import { simulaApi } from '../api/simula';
import type {
  SimulaMode,
  SimulaFinalizeAnswer,
  BreakdownEntry,
  SimulaDurability,
} from '../api/simula';
import { progressApi, type DailyActivity, type ProgressSummary } from '../api/progress';
import type { QuestionDto } from '../api/kb';
import { kbApi } from '../api/kb';
import { useChat } from '../state/chat';
import { resetForDrawerTarget } from '../state/drawer-nav';
import { useNavOverride } from '../state/nav-override';
import {
  SIMULA_DURATION_MS,
  SIMULA_QUESTION_COUNT,
  SIMULA_RESUME_VERSION,
  clearSimulaResumeSnapshot,
  loadSimulaResumeSnapshot,
  loadSimulaSeenQuestionIds,
  recordSimulaSeenQuestionIds,
  saveSimulaResumeSnapshot,
  type SimulaAnswerRecord,
  type SimulaPendingFinalizeSnapshot,
  type SimulaRunningSnapshot,
} from '../state/simula-resume-storage';
import { useSimulaSession } from '../state/simula-session';
import { explainWrongAnswerStream } from '../api/tutor';
import type { Principle } from '../types/domain';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VFAnswer = 'Vero' | 'Falso';

type AnswerRecord = SimulaAnswerRecord;

type SimulaState =
  | { phase: 'picker' }
  | { phase: 'running'; simulationId: string; mode: SimulaMode; questions: QuestionDto[]; startedAt: number; deadlineAt: number }
  | { phase: 'expired'; answeredCount: number; totalQuestions: number }
  | { phase: 'finalizing'; attempt: SimulaPendingFinalizeSnapshot }
  | { phase: 'not_saved'; attempt: SimulaPendingFinalizeSnapshot; persisted: boolean }
  | { phase: 'unverifiable'; attempt: SimulaPendingFinalizeSnapshot }
  | { phase: 'postmortem'; score: number; passed: boolean; breakdown: BreakdownEntry[]; probability: number | null; durability: SimulaDurability; masteryStatus?: 'committed' | 'legacy_unverified'; mode: SimulaMode; questions: QuestionDto[]; answers: Map<string, AnswerRecord> }
  | { phase: 'review'; mode: SimulaMode; questions: QuestionDto[]; answers: Map<string, AnswerRecord>; wrongIndices: number[]; currentReviewIdx: number; score: number; passed: boolean; breakdown: BreakdownEntry[]; probability: number | null; durability: SimulaDurability; masteryStatus?: 'committed' | 'legacy_unverified' };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOTAL_SECONDS = SIMULA_DURATION_MS / 1000;
// How long "Salvataggio risultato…" may stay in-flight before the UI abandons
// the hung request and offers the explicit retry (DP2, device pass 2026-07-13).
const FINALIZE_WATCHDOG_MS = 12_000;

// ---------------------------------------------------------------------------
// Resume-in-progress — Guidata only (Cieca stays strict "close = gone")
// ---------------------------------------------------------------------------
// Snapshotted to localStorage on every running-phase state change so a crash,
// accidental refresh, or closed tab doesn't wipe the exam. Cleared on
// finalize/reset. Timer runs in real wall time: after the official 20-minute
// window, a partial attempt expires without becoming a Progressi result, while
// a complete attempt still waits for the student's explicit Consegna.

// W2.3: key + clearSimulaResumeSnapshot moved to state/simula-resume-storage.ts
// so the shared drawer-nav reset can clear it from any screen.
function secondsUntil(deadlineAt: number, now = Date.now()): number {
  return Math.max(0, Math.ceil((deadlineAt - now) / 1000));
}

function isValidDurability(value: unknown): value is SimulaDurability {
  return value === 'saved'
    || value === 'already_saved'
    || value === 'result_saved_mastery_deferred';
}

function isExactQuestionSet(questions: readonly QuestionDto[]): boolean {
  if (questions.length !== SIMULA_QUESTION_COUNT) return false;
  const ids = new Set<string>();
  for (const question of questions) {
    if (typeof question.id !== 'string' || question.id.trim() === '' || ids.has(question.id)) return false;
    ids.add(question.id);
  }
  return ids.size === SIMULA_QUESTION_COUNT;
}

function hasExactAnswerSet(
  questions: readonly QuestionDto[],
  answersMap: ReadonlyMap<string, AnswerRecord>,
): boolean {
  return isExactQuestionSet(questions)
    && answersMap.size === SIMULA_QUESTION_COUNT
    && questions.every((question) => answersMap.has(question.id));
}

function isUnverifiableQuestionSetError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { status?: unknown; body?: unknown };
  if (candidate.status !== 409 || typeof candidate.body !== 'object' || candidate.body === null) return false;
  const body = candidate.body as { error?: unknown; code?: unknown };
  if (body.error === 'simula_question_set_unverifiable' || body.code === 'simula_question_set_unverifiable') return true;
  if (typeof body.error !== 'object' || body.error === null) return false;
  return (body.error as { code?: unknown }).code === 'simula_question_set_unverifiable';
}

// P6 STEP 3c → V28 (S4): restored 3-card picker (founder D4). `emoji` retained
// only for the legacy share-text; the V28 cards render an avatar ICON (eye-off /
// clock / layers, simula.html L64-92), NOT an emoji. `tagline` is the inline
// subtitle shown beside the title ("Cieca · come l'esame vero").
// B5 (V29 S2): each card DECLARES its resume behavior — only Guidata's running
// snapshot survives leaving; Cieca/Difficile restart from zero. The student
// learns this before starting, not by losing an exam.
const MODE_CONFIG: Record<SimulaMode, { title: string; description: string; tagline: string; resumeNote: string; emoji: string }> = {
  cieca: {
    title: 'Cieca',
    description: 'Nessun aiuto. Risultato solo alla fine. Conta per la probabilità.',
    tagline: 'come l’esame vero',
    resumeNote: 'Se esci, ricominci da capo — come all’esame.',
    emoji: '🫣',
  },
  guidata: {
    title: 'Guidata',
    description: 'Vedi giusto/sbagliato dopo ogni risposta.',
    tagline: 'con feedback',
    resumeNote: 'Riprendibile: se esci, continui da dove eri.',
    emoji: '🧭',
  },
  difficile: {
    title: 'Difficile',
    description: '30 domande dagli argomenti dove sbagli di più.',
    tagline: 'i tuoi punti deboli',
    resumeNote: 'Se esci, ricominci da capo.',
    emoji: '🔥',
  },
};

// V28 picker avatar icons (simula.html L64-92): eye-off (Cieca) / clock
// (Guidata) / layers (Difficile). Color is reinforced by shape + label, never
// color alone. `tone`: the active card tints the avatar with the brand
// gradient; resting cards use a neutral / rose fill per the prototype.
function ModeAvatar({ mode, active, t }: { mode: SimulaMode; active: boolean; t: ReturnType<typeof useTokens> }): JSX.Element {
  const fill = active
    ? t.brandGradient
    : mode === 'difficile'
    ? t.wrongSoft
    : t.surface2;
  const stroke = active ? '#fff' : mode === 'difficile' ? t.wrongText : t.accentInk;
  return (
    <span
      aria-hidden
      style={{
        flexShrink:    0,
        width:         40,
        height:        40,
        borderRadius:  12,
        background:    fill,
        display:       'flex',
        alignItems:    'center',
        justifyContent:'center',
      }}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {mode === 'cieca' && (
          <>
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
            <circle cx="12" cy="12" r="3" />
            <line x1="3" y1="3" x2="21" y2="21" />
          </>
        )}
        {mode === 'guidata' && (
          <>
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </>
        )}
        {mode === 'difficile' && (
          <>
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
          </>
        )}
      </svg>
    </span>
  );
}

// Picker card order (spec §5.5).
const MODE_ORDER: SimulaMode[] = ['cieca', 'guidata', 'difficile'];

/** "Struggling" signal for the Difficile pre-select (spec §5.5). Conservative:
 *  the student's last completed Simula was below the 27/30 pass threshold. No
 *  history → not struggling (default Cieca). */
function isStruggling(summary: ProgressSummary | null): boolean {
  const latest = latestSimulaActivity(summary);
  const score = latest?.simulaBestScore ?? null;
  return score !== null && score < 27;
}

function latestSimulaActivity(summary: ProgressSummary | null): DailyActivity | null {
  if (!summary) return null;
  return summary.recentActivity.find((day) => day.simulaSessions > 0 && day.simulaBestScore !== null) ?? null;
}

function simulaTrendLabel(summary: ProgressSummary | null): string | null {
  const scores = summary?.recentActivity
    .filter((day) => day.simulaSessions > 0 && day.simulaBestScore !== null)
    .map((day) => day.simulaBestScore as number) ?? [];
  if (scores.length < 2) return null;
  if (scores[0] > scores[1]) return 'In crescita';
  if (scores[0] < scores[1]) return 'Da recuperare';
  return 'Stabile';
}

/** V28 "Ultime 7": the last up-to-7 completed-simula day scores, OLDEST→newest
 *  for left-to-right reading. Sourced ONLY from real recentActivity
 *  (simulaBestScore on days with simulaSessions>0). No invented numbers — when
 *  the student has <2 attempts the compact strip is hidden by the caller. */
function lastSimulaScores(summary: ProgressSummary | null, max = 7): number[] {
  const scores = (summary?.recentActivity ?? [])
    .filter((day) => day.simulaSessions > 0 && day.simulaBestScore !== null)
    .map((day) => day.simulaBestScore as number);
  // recentActivity is newest-first; take the most recent `max`, then reverse to
  // oldest→newest so the strip reads chronologically.
  return scores.slice(0, max).reverse();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// W4-post-GATE-4: ModeCard kept as a comment for archival; the 3-mode picker
// was collapsed into a single-mode entry + feedback toggle. The component
// can be revived if Difficile mode earns its differentiation in W5+.
/*
interface ModeCardProps {
  mode: SimulaMode;
  onStart: (mode: SimulaMode) => void;
  loading: boolean;
}

function ModeCard({ mode, onStart, loading }: ModeCardProps): JSX.Element {
  const t = useTokens();
  const cfg = MODE_CONFIG[mode];
  return (
    <div
      style={{
        background:   t.surface,
        border:       `1px solid ${t.line}`,
        borderRadius: t.radius.lg,
        padding:      '20px 20px 16px',
        display:      'flex',
        flexDirection:'column',
        gap:          8,
      }}
    >
      <div style={{ fontSize: 28, lineHeight: 1 }}>{cfg.emoji}</div>
      <div
        style={{
          fontFamily: t.fonts.serif,
          fontSize:   18,
          fontWeight: 700,
          color:      t.text,
          letterSpacing: -0.3,
        }}
      >
        {cfg.title}
      </div>
      <div
        style={{
          fontFamily: t.fonts.body ?? t.fonts.sans,
          fontSize:   13,
          color:      t.textMuted,
          lineHeight: 1.4,
        }}
      >
        {cfg.description}
      </div>
      <button
        onClick={() => onStart(mode)}
        disabled={loading}
        style={{
          marginTop:    8,
          background:   loading ? t.surface : t.accent,
          color:        t.mode === 'dark' ? '#1a1510' : '#fff',
          border:       'none',
          borderRadius: t.radius.pill,
          padding:      '10px 18px',
          cursor:       loading ? 'not-allowed' : 'pointer',
          fontFamily:   t.fonts.body ?? t.fonts.sans,
          fontSize:     14,
          fontWeight:   600,
          alignSelf:    'flex-start',
          opacity:      loading ? 0.6 : 1,
        }}
      >
        {loading ? 'Caricamento…' : 'Inizia'}
      </button>
    </div>
  );
}
*/

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// V29 S8: the W4.6 `embedded` mode (SimulaScreen hosted inside PracticeScreen)
// is retired with the Allena complex — PracticeScreen lost its route long ago,
// so every embedded branch was unreachable. Standalone is the only Simula.

export default function SimulaScreen(): JSX.Element {
  const navigate = useNavigate();
  const t = useTokens();
  const [initialSnapshot] = useState(() => loadSimulaResumeSnapshot());
  const [state, setState] = useState<SimulaState>(() => (
    initialSnapshot?.phase === 'pending_finalize'
      ? { phase: 'not_saved', attempt: initialSnapshot, persisted: true }
      : { phase: 'picker' }
  ));
  // DP3 (founder device pass 2026-07-13): the TabBar stays visible during the
  // exam too — "the navigation bar should never disappear". Leaving mid-exam
  // is safe: the running snapshot persists continuously and the picker offers
  // Riprendi on return; a pending finalize survives any tab tap (F25).
  const [loadingMode, setLoadingMode] = useState<SimulaMode | null>(null);
  // P6 STEP 3c: restored 3-card picker (founder D4). The student selects a mode
  // card, then taps "Inizia simulazione". `null` = no explicit pick yet, so the
  // effective default is computed from `isStruggling` (Difficile when the last
  // Simula was below pass, else Cieca). Supersedes the W4 feedback toggle.
  const [selectedMode, setSelectedMode] = useState<SimulaMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progressSummary, setProgressSummary] = useState<ProgressSummary | null>(null);
  // B12 (V29 S2): null used to mean BOTH "loading" and "no history", so the
  // picker flashed "Nessuna simulazione completa ancora…" at returning students
  // on every mount. Track settlement so the card can show a skeleton instead.
  const [summarySettled, setSummarySettled] = useState(false);

  // Running state local data
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, AnswerRecord>>(new Map());
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  // FP4: per-question display timestamp. responseTimeMs must measure time spent
  // on THIS question (reset whenever a question is displayed), not time since
  // exam start — the old `now - startedAt` corrupted the speed-band grading for
  // 29 of 30 questions. Updated by the effect below on every currentIndex change.
  const questionShownAtRef = useRef<number>(Date.now());
  const [guidataFeedback, setGuidataFeedback] = useState<'right' | 'wrong' | null>(null);
  // P6 STEP 3c: in-exam 30-cell navigator overlay ("Tutte").
  const [navOpen, setNavOpen] = useState(false);
  // P6 polish #2: branded confirm modal (replaces window.confirm for consegna/abort).
  const [confirmAction, setConfirmAction] = useState<
    { title: string; body?: string; confirmLabel: string; destructive?: boolean; onConfirm: () => void } | null
  >(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Every asynchronous callback belongs to exactly one simulation lifecycle.
  // Advancing this generation makes already-queued timer/Guidata callbacks
  // harmless, even if the browser invokes them after clearInterval/reset.
  const attemptGenerationRef = useRef(0);
  const latestAnswersRef = useRef<Map<string, AnswerRecord>>(new Map());
  const currentIndexRef = useRef(0);
  const finalizeAttemptRef = useRef<SimulaPendingFinalizeSnapshot | null>(
    initialSnapshot?.phase === 'pending_finalize' ? initialSnapshot : null,
  );
  const finalizeInFlightRef = useRef<Promise<void> | null>(null);
  // Each network finalize owns one epoch. The watchdog may retire request A
  // without aborting its server-side durability work, then allow retry B to
  // replay the same frozen payload. Only the current epoch may mutate the UI.
  const finalizeRequestEpochRef = useRef(0);
  const retireFinalizeRequest = useCallback((expectedEpoch?: number): boolean => {
    if (expectedEpoch !== undefined && finalizeRequestEpochRef.current !== expectedEpoch) return false;
    finalizeRequestEpochRef.current += 1;
    finalizeInFlightRef.current = null;
    return true;
  }, []);
  const finalizeCompletedRef = useRef(false);
  // A retained attempt after failure/reload may only be submitted by the
  // explicit retry action. Late timer/Guidata callbacks must remain no-ops.
  const finalizeFailedRef = useRef(initialSnapshot?.phase === 'pending_finalize');

  // Resume candidate discovered on mount — only offered for resumable modes.
  const [resumeCandidate, setResumeCandidate] = useState<SimulaRunningSnapshot | null>(
    initialSnapshot?.phase === 'running' ? initialSnapshot : null,
  );

  // Transient toast for share copy-to-clipboard feedback.
  const [shareToast, setShareToast] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    progressApi.summary()
      .then((summary) => { if (alive) { setProgressSummary(summary); setSummarySettled(true); } })
      .catch(() => { if (alive) { setProgressSummary(null); setSummarySettled(true); } });
    return () => { alive = false; };
  }, []);

  // Finalize watchdog (founder device pass 2026-07-13): mobile Safari can
  // suspend the finalize fetch indefinitely (backgrounded tab / flaky radio) —
  // the promise never settles and the student is stuck on "Salvataggio
  // risultato…" with no way out. Abandoning a hung request is always safe
  // here: the pending snapshot is durable BEFORE the first network byte and
  // the server finalize is exact-once, so the explicit retry re-sends the
  // identical frozen payload. A late settlement from the retired request is
  // ignored by the client while the server remains free to finish its
  // exact-once write. After the watchdog fires we surface the standard
  // "Risultato non salvato" retry view.
  useEffect(() => {
    if (state.phase !== 'finalizing') return;
    const { attempt } = state;
    const requestEpoch = finalizeRequestEpochRef.current;
    const startedAtMs = Date.now();
    const dropToRetry = () => {
      if (!retireFinalizeRequest(requestEpoch)) return;
      finalizeFailedRef.current = true;
      setState((current) => (
        current.phase === 'finalizing' && current.attempt === attempt
          ? { phase: 'not_saved', attempt, persisted: true }
          : current
      ));
    };
    const timer = window.setTimeout(dropToRetry, FINALIZE_WATCHDOG_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - startedAtMs > FINALIZE_WATCHDOG_MS) {
        dropToRetry();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [retireFinalizeRequest, state]);

  // ---------------------------------------------------------------------------
  // Timer
  // ---------------------------------------------------------------------------

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const advanceAttemptGeneration = useCallback(() => {
    attemptGenerationRef.current += 1;
    return attemptGenerationRef.current;
  }, []);

  const startTimer = useCallback((
    deadlineAt: number,
    attemptGeneration: number,
    onExpire: () => void,
  ) => {
    stopTimer();
    const tick = () => {
      if (attemptGenerationRef.current !== attemptGeneration) return;
      const remaining = secondsUntil(deadlineAt);
      setSecondsLeft(remaining);
      if (remaining === 0) {
        stopTimer();
        onExpire();
      }
    };
    tick();
    if (secondsUntil(deadlineAt) > 0) timerRef.current = setInterval(tick, 1000);
  }, [stopTimer]);
  useEffect(() => () => {
    stopTimer();
    retireFinalizeRequest();
    advanceAttemptGeneration();
  }, [advanceAttemptGeneration, retireFinalizeRequest, stopTimer]);

  // FP4 per-question timing: stamp the display time whenever the visible
  // question changes (advance, skip, prev, or navigator jump) while running.
  // handleAnswer reads this ref to compute time-on-question, so the speed band
  // reflects the real per-question latency, not elapsed-since-exam-start.
  useEffect(() => {
    if (state.phase !== 'running') return;
    questionShownAtRef.current = Date.now();
  }, [currentIndex, state.phase]);

  // W2.3: explicit reset signal (fresh-start helpers). A pending finalize is
  // NEVER erased by a reset (F25 — the guard precedes every state drop).
  const simulaResetCounter = useSimulaSession((s) => s.resetCounter);
  useEffect(() => {
    if (simulaResetCounter === 0) return; // initial mount, no-op
    if (finalizeAttemptRef.current) return;
    stopTimer();
    retireFinalizeRequest();
    advanceAttemptGeneration();
    setState({ phase: 'picker' });
    setLoadingMode(null);
    setError(null);
    currentIndexRef.current = 0;
    latestAnswersRef.current = new Map();
    setCurrentIndex(0);
    setAnswers(latestAnswersRef.current);
    setSecondsLeft(TOTAL_SECONDS);
    setGuidataFeedback(null);
    setNavOpen(false);
    setConfirmAction(null);
    setResumeCandidate(null);
  }, [advanceAttemptGeneration, retireFinalizeRequest, simulaResetCounter, stopTimer]);

  // ---------------------------------------------------------------------------
  // Finalize helper
  // ---------------------------------------------------------------------------

  const submitFinalizeAttempt = useCallback((
    attempt: SimulaPendingFinalizeSnapshot,
    explicitRetry: boolean,
  ): Promise<void> => {
    if (finalizeCompletedRef.current) return Promise.resolve();
    if (finalizeInFlightRef.current) return finalizeInFlightRef.current;
    if (finalizeFailedRef.current && !explicitRetry) return Promise.resolve();

    stopTimer();
    finalizeAttemptRef.current = attempt;
    // The recovery snapshot must be durable before the first network byte. If
    // storage is unavailable, remain retry-safe in memory and do not submit.
    if (!saveSimulaResumeSnapshot(attempt)) {
      finalizeFailedRef.current = true;
      setState({ phase: 'not_saved', attempt, persisted: false });
      return Promise.resolve();
    }

    finalizeFailedRef.current = false;
    setState({ phase: 'finalizing', attempt });
    const requestEpoch = finalizeRequestEpochRef.current + 1;
    finalizeRequestEpochRef.current = requestEpoch;
    const requestAttemptGeneration = attemptGenerationRef.current;
    const isCurrentRequest = () => (
      finalizeRequestEpochRef.current === requestEpoch
      && attemptGenerationRef.current === requestAttemptGeneration
    );
    const request = simulaApi.finalize(attempt.simulationId, attempt.mode, attempt.payload)
      .then((result) => {
        if (!isCurrentRequest()) return;
        if (!isValidDurability(result.durability) || !Number.isInteger(result.score) || result.score < 0 || result.score > 30
          || typeof result.passed !== 'boolean' || result.passed !== (result.score >= 27)
          || !Array.isArray(result.breakdown) || !(result.probability === null
            || (typeof result.probability === 'number' && Number.isFinite(result.probability) && result.probability >= 0 && result.probability <= 0.9))
          || (result.mastery_status !== undefined && !['committed', 'legacy_unverified'].includes(result.mastery_status))) {
          throw new Error('simula_finalize_invalid_acknowledgement');
        }
        clearSimulaResumeSnapshot();
        finalizeAttemptRef.current = null;
        finalizeFailedRef.current = false;
        finalizeCompletedRef.current = true;
        advanceAttemptGeneration();
        setState({
          phase:       'postmortem',
          score:       result.score,
          passed:      result.passed,
          breakdown:   result.breakdown,
          probability: result.probability,
          durability:  result.durability,
          masteryStatus: result.mastery_status,
          mode:         attempt.mode,
          questions:    attempt.questions,
          answers:      new Map(attempt.answers),
        });
      })
      .catch((requestError: unknown) => {
        if (!isCurrentRequest()) return;
        finalizeFailedRef.current = true;
        setState(isUnverifiableQuestionSetError(requestError)
          ? { phase: 'unverifiable', attempt }
          : { phase: 'not_saved', attempt, persisted: true });
      })
      .finally(() => {
        if (
          finalizeRequestEpochRef.current === requestEpoch
          && finalizeInFlightRef.current === request
        ) finalizeInFlightRef.current = null;
      });
    finalizeInFlightRef.current = request;
    return request;
  }, [advanceAttemptGeneration, stopTimer]);

  const finalize = useCallback((
    simulationId: string,
    mode: SimulaMode,
    questions: QuestionDto[],
    answersMap: Map<string, AnswerRecord>,
    startedAt: number,
    deadlineAt: number,
    explicitRetry = false,
  ): Promise<void> => {
    // Defense in depth: the overview blocks this action too, but no client-side
    // callback may manufacture a completed result from a partial attempt.
    if (!hasExactAnswerSet(questions, answersMap)) return Promise.resolve();
    if (finalizeCompletedRef.current) return Promise.resolve();
    if (finalizeInFlightRef.current) return finalizeInFlightRef.current;
    const retained = finalizeAttemptRef.current;
    if (retained) return submitFinalizeAttempt(retained, explicitRetry);
    if (explicitRetry) return Promise.resolve();

    const frozenQuestions = questions.map((question) => ({
      ...question,
      argomenti: question.argomenti ? { ...question.argomenti } : undefined,
    }));
    const frozenAnswers = [...answersMap.entries()].map(([questionId, record]) => (
      [questionId, { ...record }] as [string, AnswerRecord]
    ));
    const frozenAnswerMap = new Map(frozenAnswers);
    const finalAnswers: SimulaFinalizeAnswer[] = frozenQuestions.map((question) => {
      const record = frozenAnswerMap.get(question.id);
      return {
        questionId: question.id,
        answer: record ? record.answer === 'Vero' : false,
        correctAnswer: question.is_true,
        responseTimeMs: record?.responseTimeMs ?? 0,
        answered: !!record,
      };
    });
    const attempt: SimulaPendingFinalizeSnapshot = {
      version: SIMULA_RESUME_VERSION,
      phase: 'pending_finalize',
      simulationId,
      mode,
      questions: frozenQuestions,
      startedAt,
      deadlineAt,
      currentIndex: Math.max(0, Math.min(currentIndexRef.current, frozenQuestions.length - 1)),
      answers: frozenAnswers,
      payload: finalAnswers,
      savedAt: Date.now(),
    };
    finalizeAttemptRef.current = attempt;
    return submitFinalizeAttempt(attempt, false);
  }, [submitFinalizeAttempt]);

  const handleDeadlineReached = useCallback((
    questions: QuestionDto[],
    answersMap: Map<string, AnswerRecord>,
  ) => {
    stopTimer();
    setSecondsLeft(0);
    if (hasExactAnswerSet(questions, answersMap)) {
      // Even at the deadline, a complete attempt is shown for review and only
      // becomes a result after the student explicitly presses Consegna.
      setNavOpen(true);
      return;
    }
    clearSimulaResumeSnapshot();
    setResumeCandidate(null);
    advanceAttemptGeneration();
    setNavOpen(false);
    setState({
      phase: 'expired',
      answeredCount: answersMap.size,
      totalQuestions: questions.length,
    });
  }, [advanceAttemptGeneration, stopTimer]);

  // Browser timers may be throttled or suspended while a tab is backgrounded.
  // Reconcile against the absolute deadline as soon as the document becomes
  // visible, using the latest answer ref rather than a render-time snapshot.
  useEffect(() => {
    if (state.phase !== 'running') return;
    const attemptGeneration = attemptGenerationRef.current;
    const reconcileDeadline = () => {
      if (
        document.visibilityState !== 'visible'
        || attemptGenerationRef.current !== attemptGeneration
        || Date.now() < state.deadlineAt
      ) return;
      handleDeadlineReached(state.questions, latestAnswersRef.current);
    };
    document.addEventListener('visibilitychange', reconcileDeadline);
    return () => document.removeEventListener('visibilitychange', reconcileDeadline);
  }, [handleDeadlineReached, state]);

  // ---------------------------------------------------------------------------
  // Start simulation
  // ---------------------------------------------------------------------------

  async function handleStart(mode: SimulaMode) {
    setSelectedMode(mode);
    setLoadingMode(mode);
    setError(null);
    try {
      // FP4: hold back recently-seen questions so consecutive exams don't repeat
      // now that the backend samples the full pool at random. Empty list ⇒ no
      // second argument (keeps the start() call shape identical for fresh users).
      const seen = loadSimulaSeenQuestionIds();
      const res = seen.length > 0
        ? await simulaApi.start(mode, seen)
        : await simulaApi.start(mode);
      if (!isExactQuestionSet(res.questions)) throw new Error('simula_start_invalid_question_set');
      // Record this exam's questions as recently-seen for the NEXT start.
      recordSimulaSeenQuestionIds(res.questions.map((q) => q.id));
      const now = Date.now();
      const deadlineAt = now + SIMULA_DURATION_MS;
      // Only a successful replacement may discard the previous resume.
      retireFinalizeRequest();
      clearSimulaResumeSnapshot();
      setResumeCandidate(null);
      const attemptGeneration = advanceAttemptGeneration();
      finalizeAttemptRef.current = null;
      finalizeFailedRef.current = false;
      finalizeCompletedRef.current = false;
      currentIndexRef.current = 0;
      latestAnswersRef.current = new Map();
      setCurrentIndex(0);
      setAnswers(latestAnswersRef.current);
      setSecondsLeft(TOTAL_SECONDS);
      setGuidataFeedback(null);
      setState({
        phase:        'running',
        simulationId: res.simulationId,
        mode,
        questions:    res.questions,
        startedAt:    now,
        deadlineAt,
      });
      const finalizeOnExpiry = () => {
        handleDeadlineReached(res.questions, latestAnswersRef.current);
      };
      startTimer(deadlineAt, attemptGeneration, finalizeOnExpiry);
    } catch {
      setError('La simulazione non è partita. Riprova tra qualche secondo.');
    } finally {
      setLoadingMode(null);
    }
  }

  // (V29 S8: the embedded auto-start effect died with the Allena complex —
  // standalone Simula always begins at its picker.)

  // ---------------------------------------------------------------------------
  // Answer handler
  // ---------------------------------------------------------------------------

  function handleAnswer(vf: VFAnswer) {
    if (state.phase !== 'running') return;
    const q = state.questions[currentIndex];
    if (!q) return;

    const now = Date.now();
    if (now >= state.deadlineAt) {
      handleDeadlineReached(state.questions, latestAnswersRef.current);
      return;
    }
    // FP4: time spent on THIS question, not elapsed-since-exam-start. Clamp at 0
    // to guard against any clock skew / ref staleness.
    const responseTimeMs = Math.max(0, now - questionShownAtRef.current);
    const newAnswers = new Map(latestAnswersRef.current);
    newAnswers.set(q.id, { answer: vf, responseTimeMs, answeredAt: now });
    latestAnswersRef.current = newAnswers;
    setAnswers(newAnswers);
    if (state.mode === 'guidata') {
      const isRight = (vf === 'Vero') === q.is_true;
      const attemptGeneration = attemptGenerationRef.current;
      setGuidataFeedback(isRight ? 'right' : 'wrong');
      setTimeout(() => {
        if (attemptGenerationRef.current !== attemptGeneration) return;
        setGuidataFeedback(null);
        advanceQuestion(state.questions, q.id);
      }, 1500);
    } else {
      advanceQuestion(state.questions, q.id);
    }

    // Fire-and-forget answer record to backend (stateless MVP — just for logging)
    void simulaApi.answer(state.simulationId, {
      questionId:    q.id,
      answer:        vf === 'Vero',
      responseTimeMs,
    }).catch(() => {/* ignore */});
  }

  function advanceQuestion(
    questions: QuestionDto[],
    answeredQuestionId: string,
  ) {
    const answeredIndex = questions.findIndex((question) => question.id === answeredQuestionId);
    if (answeredIndex < 0) return;
    const next = answeredIndex + 1;
    if (next >= questions.length) {
      setNavOpen(true);
      return;
    }
    currentIndexRef.current = next;
    setCurrentIndex(next);
  }
  function handleSkip() {
    if (state.phase !== 'running') return;
    setCurrentIndex((idx) => {
      const next = Math.min(idx + 1, state.questions.length - 1);
      currentIndexRef.current = next;
      return next;
    });
    setGuidataFeedback(null);
  }

  function handlePrev() {
    setCurrentIndex((idx) => {
      const next = Math.max(idx - 1, 0);
      currentIndexRef.current = next;
      return next;
    });
    setGuidataFeedback(null);
  }

  // ---------------------------------------------------------------------------
  // Nav-actions registry → move BETWEEN QUESTIONS (running phase only).
  // V28 round-2 registered these for swipe/edge-tap; V29 S8 removed those
  // gestures (EdgeNavZones/SwipeHint deleted) — the registry remains the
  // shared onBack/onNext contract consumed by useShellNav (chrome back) and
  // any future hardware-back wiring. Clamping lives in handlePrev/handleSkip.
  // Registered ONLY in the running phase (picker / postmortem / review
  // register nothing) and cleared on unmount / phase change so the override
  // never leaks onto another screen.
  // ---------------------------------------------------------------------------
  const { setNavActions } = useNavOverride();
  const runningPhase = state.phase === 'running';
  useEffect(() => {
    if (!runningPhase) {
      setNavActions({});
      return;
    }
    setNavActions({ onBack: handlePrev, onNext: handleSkip });
    return () => setNavActions({});
    // handlePrev/handleSkip read the latest currentIndex via functional state
    // updaters; re-register when the question set changes so the clamp uses the
    // current length. setNavActions is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningPhase, state, setNavActions]);

  // ---------------------------------------------------------------------------
  // Reset to picker
  // ---------------------------------------------------------------------------

  function handleReset() {
    stopTimer();
    retireFinalizeRequest();
    advanceAttemptGeneration();
    clearSimulaResumeSnapshot();
    finalizeAttemptRef.current = null;
    finalizeFailedRef.current = false;
    finalizeCompletedRef.current = false;
    setResumeCandidate(null);
    setState({ phase: 'picker' });
    currentIndexRef.current = 0;
    latestAnswersRef.current = new Map();
    setCurrentIndex(0);
    setAnswers(latestAnswersRef.current);
    setSecondsLeft(TOTAL_SECONDS);
    setGuidataFeedback(null);
    setNavOpen(false);
    setConfirmAction(null);
    setError(null);
  }

  // ---------------------------------------------------------------------------
  // Resume snapshot: write on every running-phase state change (Guidata only).
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (state.phase !== 'running') return;
    if (state.mode !== 'guidata') return;
    saveSimulaResumeSnapshot({
      version:      SIMULA_RESUME_VERSION,
      phase:        'running',
      simulationId: state.simulationId,
      mode:         state.mode,
      questions:    state.questions,
      startedAt:    state.startedAt,
      deadlineAt:   state.deadlineAt,
      currentIndex,
      answers:      [...answers.entries()],
      savedAt:      Date.now(),
    });
  }, [state, currentIndex, answers]);

  // ---------------------------------------------------------------------------
  // Resume handler — restore state from snapshot + honest timer recompute.
  // ---------------------------------------------------------------------------

  const handleResume = useCallback(() => {
    if (!resumeCandidate) return;
    const snap = resumeCandidate;
    const remaining = secondsUntil(snap.deadlineAt);
    const restoredAnswers = new Map<string, AnswerRecord>(snap.answers);
    latestAnswersRef.current = restoredAnswers;
    currentIndexRef.current = snap.currentIndex;
    const attemptGeneration = advanceAttemptGeneration();
    const finalizeOnExpiry = () => {
      handleDeadlineReached(snap.questions, latestAnswersRef.current);
    };

    // An already-expired partial attempt cannot resume or become a result. A
    // complete one reopens at the overview and still waits for Consegna.
    if (remaining <= 0) {
      setResumeCandidate(null);
      setCurrentIndex(snap.currentIndex);
      setAnswers(restoredAnswers);
      setSecondsLeft(0);
      setGuidataFeedback(null);
      if (restoredAnswers.size !== snap.questions.length) {
        handleDeadlineReached(snap.questions, restoredAnswers);
        return;
      }
      setState({
        phase:        'running',
        simulationId: snap.simulationId,
        mode:         snap.mode,
        questions:    snap.questions,
        startedAt:    snap.startedAt,
        deadlineAt:   snap.deadlineAt,
      });
      setNavOpen(true);
      return;
    }

    setResumeCandidate(null);
    setCurrentIndex(snap.currentIndex);
    setAnswers(restoredAnswers);
    setSecondsLeft(remaining);
    setGuidataFeedback(null);
    setState({
      phase:        'running',
      simulationId: snap.simulationId,
      mode:         snap.mode,
      questions:    snap.questions,
      startedAt:    snap.startedAt,
      deadlineAt:   snap.deadlineAt,
    });
    if (restoredAnswers.size === snap.questions.length) setNavOpen(true);
    startTimer(snap.deadlineAt, attemptGeneration, finalizeOnExpiry);
  }, [advanceAttemptGeneration, handleDeadlineReached, resumeCandidate, startTimer]);

  function handleDismissResume() {
    clearSimulaResumeSnapshot();
    advanceAttemptGeneration();
    setResumeCandidate(null);
  }
  async function handleShareScore() {
    if (state.phase !== 'postmortem') return;
    const total = state.questions.length;
    const boundedScore = Math.max(0, Math.min(total, Math.floor(state.score)));
    const nonStandardPerfect = total !== 30 && boundedScore === total;
    const modeLabel = state.mode === 'cieca' ? 'Cieca' : state.mode === 'guidata' ? 'Guidata' : 'Difficile';
    const positiveResult = state.passed || nonStandardPerfect;
    const emoji = positiveResult ? '🎯' : '💪';
    const verdict = nonStandardPerfect
      ? 'Risultato perfetto nella simulazione non standard.'
      : state.passed
        ? 'Promosso!'
        : 'Ci riprovo.';
    const text =
      `${emoji} Ho fatto ${boundedScore}/${total} al Simula ${modeLabel} della patente B su Il Prof. ${verdict}`;
    try {
      const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
      if (typeof nav.share === 'function') {
        await nav.share({ title: 'Il mio risultato', text });
        return;
      }
    } catch {
      // User cancelled the native share sheet — fall through to clipboard.
    }
    try {
      await navigator.clipboard.writeText(text);
      setShareToast('Copiato negli appunti ✓');
      window.setTimeout(() => setShareToast(null), 2400);
    } catch {
      setShareToast('Copia non riuscita — prova a lungo-premere il testo del risultato.');
      window.setTimeout(() => setShareToast(null), 3000);
    }
  }

  // ---------------------------------------------------------------------------
  // Post-mortem → Review mode transition
  // Computes which questions were wrong, transitions to review phase.
  // ---------------------------------------------------------------------------

  // Optional `argomentoName` (P6 polish #1): when a post-mortem breakdown row is
  // clicked, jump the review straight to that argomento's first wrong answer.
  function handleStartReview(argomentoName?: string) {
    if (state.phase !== 'postmortem') return;
    const wrongIndices: number[] = [];
    state.questions.forEach((q, i) => {
      const rec = state.answers.get(q.id);
      const studentBool = rec ? rec.answer === 'Vero' : false;
      const studentAnswered = !!rec;
      // "Wrong" = answered incorrectly. Unanswered counts as wrong too (matches
      // the Simula scoring rule) so the review walks those as well.
      if (!studentAnswered || studentBool !== q.is_true) wrongIndices.push(i);
    });
    if (wrongIndices.length === 0) return; // 30/30 — the Rivedi entry points are hidden anyway.
    let currentReviewIdx = 0;
    if (argomentoName) {
      const pos = wrongIndices.findIndex(
        (qi) => state.questions[qi].argomenti?.name_it === argomentoName,
      );
      if (pos >= 0) currentReviewIdx = pos;
    }
    setState({
      phase:            'review',
      mode:             state.mode,
      questions:        state.questions,
      answers:          state.answers,
      wrongIndices,
      currentReviewIdx,
      score:            state.score,
      passed:           state.passed,
      breakdown:        state.breakdown,
      probability:      state.probability,
      durability:       state.durability,
      masteryStatus: state.masteryStatus,
    });
  }

  // ---------------------------------------------------------------------------
  // Review mode back to post-mortem
  // ---------------------------------------------------------------------------

  function handleReviewBack() {
    if (state.phase !== 'review') return;
    setState({
      phase:       'postmortem',
      mode:        state.mode,
      questions:   state.questions,
      answers:     state.answers,
      score:       state.score,
      passed:      state.passed,
      breakdown:   state.breakdown,
      probability: state.probability,
      durability:  state.durability,
      masteryStatus: state.masteryStatus,
    });
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const timerDisplay = formatTime(secondsLeft);
  const timerUrgent = secondsLeft <= 60;      // red banner + pulse on timer chip
  const timerAdvisory = secondsLeft <= 300;   // amber color on timer chip (5 min)

  // Screen-owned chrome (V29 F8a: the 1/sec timer must re-render only this
  // tree). Defined as a plain render fn (NOT a nested component) so React
  // doesn't re-mount the children on every render.
  // V29 S1 (amended by DP3): the picker is the section root (profile chip);
  // the other phases drop the profile chip but keep the TabBar — the bottom
  // nav is permanent on every student screen.
  const renderShell = (
    args: {
      title: string;
      subtitle?: string;
      timer?: string;
      timerState?: 'normal' | 'advisory' | 'urgent';
      children: React.ReactNode;
    },
  ): JSX.Element => {
    return (
      <Screen>
        <TopChrome
          title={args.title}
          subtitle={args.subtitle}
          mode="simula"
          timer={args.timer}
          timerState={args.timerState}
          profileChip={state.phase === 'picker'}
        />
        {args.children}
      </Screen>
    );
  };

  if (state.phase === 'finalizing') {
    return renderShell({
      title: 'Simula Esame',
      subtitle: 'Salvataggio in corso',
      children: (
        <div
          data-testid="simula-finalizing"
          role="status"
          aria-live="polite"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            color: t.text,
            fontFamily: t.fonts.body ?? t.fonts.sans,
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          Salvataggio risultato…
        </div>
      ),
    });
  }

  if (state.phase === 'not_saved') {
    return renderShell({
      title: 'Simula Esame',
      subtitle: 'Salvataggio interrotto',
      children: (
        <div
          data-testid="simula-not-saved"
          role="alert"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 12,
            padding: 24,
            fontFamily: t.fonts.body ?? t.fonts.sans,
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 800, color: t.wrongText }}>
            Risultato non salvato
          </div>
          <p style={{ margin: 0, color: t.textMuted, lineHeight: 1.5 }}>
            {state.persisted
              ? 'Le tue risposte sono al sicuro su questo dispositivo. Riprova il salvataggio senza rifare la simulazione.'
              : 'Il tentativo esiste solo in questa pagina. Tienila aperta e riprova il salvataggio.'}
          </p>
          <button
            onClick={() => { void submitFinalizeAttempt(state.attempt, true); }}
            style={{
              alignSelf: 'flex-start',
              minHeight: 44,
              padding: '11px 18px',
              border: 'none',
              borderRadius: t.radius.md,
              background: t.accent,
              color: t.mode === 'dark' ? '#1a1510' : '#fff',
              fontFamily: t.fonts.body ?? t.fonts.sans,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Riprova il salvataggio
          </button>
        </div>
      ),
    });
  }

  if (state.phase === 'unverifiable') {
    return renderShell({
      title: 'Simula Esame',
      subtitle: 'Tentativo non verificabile',
      children: (
        <div
          data-testid="simula-unverifiable"
          role="alert"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 12,
            padding: 24,
            fontFamily: t.fonts.body ?? t.fonts.sans,
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 800, color: t.wrongText }}>
            Questa simulazione non può essere verificata
          </div>
          <p style={{ margin: 0, color: t.textMuted, lineHeight: 1.5 }}>
            È un tentativo salvato con una versione precedente. Per proteggere il risultato non verrà inviato di nuovo.
          </p>
          <button
            onClick={handleReset}
            style={{
              alignSelf: 'flex-start',
              minHeight: 44,
              padding: '11px 18px',
              border: 'none',
              borderRadius: t.radius.md,
              background: t.accent,
              color: t.mode === 'dark' ? '#1a1510' : '#fff',
              fontFamily: t.fonts.body ?? t.fonts.sans,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Scarta e ricomincia
          </button>
        </div>
      ),
    });
  }

  if (state.phase === 'expired') {
    return renderShell({
      title: 'Tempo scaduto',
      subtitle: 'Simula Esame',
      children: (
        <div
          data-testid="simula-expired"
          role="status"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 12,
            padding: 24,
            fontFamily: t.fonts.body ?? t.fonts.sans,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 800, color: t.text }}>
            Tempo scaduto
          </div>
          <p style={{ margin: 0, color: t.textMuted, lineHeight: 1.5 }}>
            Hai risposto a {state.answeredCount} {state.answeredCount === 1 ? 'domanda' : 'domande'} su {state.totalQuestions}. Questa prova non è stata registrata nei Progressi.
          </p>
          <button
            onClick={handleReset}
            style={{
              alignSelf: 'flex-start',
              minHeight: 44,
              padding: '11px 18px',
              border: 'none',
              borderRadius: t.radius.md,
              background: t.accent,
              color: t.mode === 'dark' ? '#1a1510' : '#fff',
              fontFamily: t.fonts.body ?? t.fonts.sans,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Nuova simulazione
          </button>
        </div>
      ),
    });
  }

  // ---------------------------------------------------------------------------
  // Render: State 1 — Mode picker
  // ---------------------------------------------------------------------------

  if (state.phase === 'picker') {
    const latestSimula = latestSimulaActivity(progressSummary);
    const latestScore = latestSimula?.simulaBestScore ?? null;
    const trend = simulaTrendLabel(progressSummary);
    // P6 STEP 3c: Difficile is pre-selected when the student is struggling
    // (spec §5.5). Effective mode = explicit pick, else struggling→difficile.
    const struggling = isStruggling(progressSummary);
    const effectiveMode: SimulaMode = selectedMode ?? (struggling ? 'difficile' : 'cieca');
    return renderShell({
      title: 'Simula Esame',
      subtitle: '30 domande',
      children: (
        <div
          style={{
            flex:      1,
            overflow:  'auto',
            padding:   '16px 16px 24px',
            display:   'flex',
            flexDirection: 'column',
            gap:       12,
          }}
        >
          {resumeCandidate && (() => {
            const remaining = secondsUntil(resumeCandidate.deadlineAt);
            const answeredCount = resumeCandidate.answers.length;
            const remainingLabel = remaining === 0
              ? answeredCount === SIMULA_QUESTION_COUNT
                ? 'tempo scaduto — pronta da consegnare'
                : 'tempo scaduto — prova incompleta'
              : `~${Math.ceil(remaining / 60)} min rimasti`;
            const modeLabel = MODE_CONFIG[resumeCandidate.mode].title;
            return (
              <div
                style={{
                  background:   t.accentSoft,
                  border:       `1px solid ${t.accent}`,
                  borderRadius: t.radius.lg,
                  padding:      '14px 16px',
                  display:      'flex',
                  flexDirection:'column',
                  gap:          10,
                }}
              >
                <div>
                  <div style={{ fontFamily: t.fonts.serif, fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 4 }}>
                    Hai una simulazione {modeLabel} interrotta
                  </div>
                  <div style={{ fontFamily: t.fonts.body ?? t.fonts.sans, fontSize: 13, color: t.textMuted, lineHeight: 1.4 }}>
                    {answeredCount} {answeredCount === 1 ? 'risposta data' : 'risposte date'} · {remainingLabel}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={handleResume}
                    style={{
                      background:   t.accent,
                      color:        t.mode === 'dark' ? '#1a1510' : '#fff',
                      border:       'none',
                      borderRadius: t.radius.pill,
                      padding:      '10px 18px',
                      fontSize:     14,
                      fontWeight:   600,
                      cursor:       'pointer',
                      minHeight:    44,
                    }}
                  >
                    Riprendi ↻
                  </button>
                  <button
                    onClick={handleDismissResume}
                    style={{
                      background:   'transparent',
                      color:        t.text,
                      border:       `1px solid ${t.lineStrong}`,
                      borderRadius: t.radius.pill,
                      padding:      '10px 18px',
                      fontSize:     14,
                      fontWeight:   500,
                      cursor:       'pointer',
                      minHeight:    44,
                    }}
                  >
                    {/* B5 (V29 S2): honest label — this only DISCARDS the saved
                        attempt; it does not start a new exam. Was "Ricomincia
                        da capo", which promised a restart it never did. */}
                    Scarta
                  </button>
                </div>
              </div>
            );
          })()}
          {(() => {
            // V28 "Ultime 7": compressed status card. Per guard §1 #13 the
            // recent attempts are a COMPACT summary — an inline figure for the
            // last score + a small dot strip (sage ≥27 / rose <27) of the last
            // up-to-7 completed simulas — NOT a sprawling list. Every figure is
            // real recentActivity data (no invented numbers).
            const last7 = lastSimulaScores(progressSummary);
            const best = progressSummary?.bestSimulaScore ?? null;
            return (
              <section
                data-testid="simula-standalone-summary"
                aria-label="Riepilogo Simula Esame"
                style={{
                  background:   t.surface,
                  border:       `1px solid ${t.line}`,
                  borderRadius: t.radius.lg,
                  padding:      '14px 16px',
                  display:      'flex',
                  flexDirection:'column',
                  gap:          12,
                  fontFamily:   t.fonts.body ?? t.fonts.sans,
                }}
              >
                {!summarySettled ? (
                  /* B12 (V29 S2): skeleton while the summary fetch is in
                     flight — never flash the empty-history copy at a returning
                     student. Two shimmering rows sized like the real content. */
                  <div data-testid="simula-summary-skeleton" aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ height: 34, width: '55%', borderRadius: 8, background: t.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
                    <div style={{ height: 14, width: '80%', borderRadius: 7, background: t.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
                  </div>
                ) : latestScore !== null ? (
                  <>
                    {/* Top row: last-score figure + verdict + best/trend inline */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{ flexShrink: 0 }}>
                        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 2 }}>
                          Ultima simulazione
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: t.text, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                          {latestScore}<span style={{ fontSize: 14, fontWeight: 600, color: t.textMuted }}>/30</span>
                        </div>
                        <div style={{ marginTop: 5, fontSize: 12.5, fontWeight: 700, color: latestScore >= 27 ? t.rightText : t.wrongText }}>
                          {latestScore >= 27 ? 'Passata' : 'Da rinforzare'}
                        </div>
                      </div>
                      <div
                        style={{
                          flex:         1,
                          minWidth:     0,
                          alignSelf:    'stretch',
                          paddingLeft:  14,
                          borderLeft:   `1px solid ${t.line}`,
                          display:      'flex',
                          flexDirection:'column',
                          justifyContent:'center',
                          gap:          4,
                          fontSize:     12.5,
                          color:        t.textMuted,
                        }}
                      >
                        {best !== null && (
                          <span>Miglior punteggio <strong style={{ color: best >= 27 ? t.rightText : t.text }}>{best}/30</strong></span>
                        )}
                        {trend && (
                          <span data-testid="simula-trend">Trend <strong style={{ color: t.text }}>{trend}</strong></span>
                        )}
                        <span style={{ color: t.textFaint, fontSize: 11.5 }}>30 domande · 20 min · soglia 27/30</span>
                      </div>
                    </div>

                    {/* Compact "Ultime 7" dot strip — one tiny figure per
                        completed simula, oldest→newest. Color (sage/rose) is
                        reinforced by the printed score, never color alone. */}
                    {last7.length >= 2 && (
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: t.textFaint, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>
                          Ultime {last7.length}
                        </div>
                        <div
                          role="img"
                          aria-label={`Ultime ${last7.length} simulazioni: ${last7.join(', ')} su 30`}
                          style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
                        >
                          {last7.map((sc, i) => {
                            const ok = sc >= 27;
                            return (
                              <span
                                key={i}
                                title={`${sc}/30`}
                                style={{
                                  minWidth:     26,
                                  height:       22,
                                  padding:      '0 5px',
                                  borderRadius: t.radius.sm,
                                  background:   ok ? t.rightSoft : t.wrongSoft,
                                  color:        ok ? t.rightText : t.wrongText,
                                  border:       `1px solid ${ok ? 'rgba(46,141,111,0.45)' : 'rgba(197,54,91,0.45)'}`,
                                  fontSize:     11,
                                  fontWeight:   700,
                                  fontVariantNumeric: 'tabular-nums',
                                  display:      'flex',
                                  alignItems:   'center',
                                  justifyContent:'center',
                                }}
                              >
                                {sc}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.45 }}>
                    Nessuna simulazione completa ancora. Parti da qui quando vuoi misurare la tua prontezza.
                    <span style={{ display: 'block', marginTop: 4, color: t.textFaint, fontSize: 12 }}>30 domande · 20 min · soglia 27/30</span>
                  </div>
                )}
              </section>
            );
          })()}
          {/* P6 STEP 3c (founder D4): restored 3-card mode picker
              (Cieca / Guidata / Difficile, no emoji per spec §5.5). Difficile
              is pre-selected when the student is struggling, with a full-width
              Prof nudge toward Progressi. The backend already implements all
              three modes (difficile = weak-argomento weighting). */}
          {struggling && (
            <div
              data-testid="simula-struggling-nudge"
              style={{
                background:   t.accentSoft,
                border:       `1px solid ${t.accent}`,
                borderRadius: t.radius.lg,
                padding:      '14px 16px',
                display:      'flex',
                flexDirection:'column',
                gap:          10,
              }}
            >
              <div style={{ fontFamily: t.fonts.body ?? t.fonts.sans, fontSize: 14, color: t.text, lineHeight: 1.5 }}>
                L’ultima simulazione è andata sotto la soglia. Ti conviene allenarti sui tuoi argomenti
                più deboli: ho già selezionato la modalità <strong>Difficile</strong>.
              </div>
              <button
                onClick={() => { resetForDrawerTarget('progresso'); navigate('/progresso'); }}
                style={{
                  alignSelf:    'flex-start',
                  minHeight:    44,
                  background:   'transparent',
                  border:       `1px solid ${t.accent}`,
                  borderRadius: t.radius.pill,
                  padding:      '9px 16px',
                  color:        t.accentInk,
                  fontFamily:   t.fonts.body ?? t.fonts.sans,
                  fontSize:     13,
                  fontWeight:   600,
                  cursor:       'pointer',
                }}
              >
                Vai ai Progressi →
              </button>
            </div>
          )}

          <div style={{ fontFamily: t.fonts.body ?? t.fonts.sans, fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: 0.6, textTransform: 'uppercase' }}>
            Come vuoi farla?
          </div>
          <div role="radiogroup" aria-label="Modalità simulazione" data-testid="simula-mode-picker" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {MODE_ORDER.map((m) => {
              const cfg = MODE_CONFIG[m];
              const active = effectiveMode === m;
              return (
                <button
                  key={m}
                  role="radio"
                  aria-checked={active}
                  data-testid={`simula-mode-card-${m}`}
                  onClick={() => setSelectedMode(m)}
                  style={{
                    textAlign:    'left',
                    background:   active ? t.accentSoft : t.surface,
                    border:       `${active ? 1.5 : 1}px solid ${active ? t.accent : t.line}`,
                    borderRadius: t.radius.lg,
                    padding:      '14px 16px',
                    cursor:       'pointer',
                    display:      'flex',
                    alignItems:   'flex-start',
                    gap:          12,
                    transition:   'background .15s, border-color .15s',
                  }}
                >
                  <ModeAvatar mode={m} active={active} t={t} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: t.fonts.body ?? t.fonts.sans, fontSize: 15, fontWeight: 700, color: t.text, letterSpacing: -0.1 }}>
                      {cfg.title} <span style={{ fontWeight: 500, color: t.textMuted }}>· {cfg.tagline}</span>
                    </span>
                    <span style={{ display: 'block', fontFamily: t.fonts.body ?? t.fonts.sans, fontSize: 13, color: t.textMuted, lineHeight: 1.45, marginTop: 4 }}>
                      {cfg.description}
                    </span>
                    {/* B5 (V29 S2): the resume asymmetry, declared up front. */}
                    <span
                      data-testid={`simula-resume-note-${m}`}
                      style={{ display: 'block', fontFamily: t.fonts.body ?? t.fonts.sans, fontSize: 11.5, color: m === 'guidata' ? t.rightText : t.textFaint, lineHeight: 1.4, marginTop: 4, fontWeight: 600 }}
                    >
                      {cfg.resumeNote}
                    </span>
                  </span>
                </button>
              );
            })}
            <button
              data-testid="simula-start"
              onClick={() => handleStart(effectiveMode)}
              disabled={loadingMode !== null}
              style={{
                marginTop:    4,
                display:      'flex',
                alignItems:   'center',
                justifyContent: 'center',
                gap:          8,
                background:   loadingMode !== null ? t.line : t.brandGradient,
                color:        loadingMode !== null ? t.textFaint : '#fff',
                border:       'none',
                borderRadius: t.radius.md,
                padding:      '14px 22px',
                fontFamily:   t.fonts.body ?? t.fonts.sans,
                fontSize:     15,
                fontWeight:   700,
                cursor:       loadingMode !== null ? 'wait' : 'pointer',
                minHeight:    44,
                letterSpacing: -0.05,
                boxShadow:    loadingMode !== null ? 'none' : '0 3px 12px rgba(105,67,255,0.28)',
              }}
            >
              {loadingMode !== null ? 'Caricamento...' : 'Inizia simulazione'}
              {loadingMode === null && (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>
          </div>
          {error && (
            <div
              style={{
                color:      t.wrong,
                fontFamily: t.fonts.body ?? t.fonts.sans,
                fontSize:   13,
                padding:    '8px 12px',
                background: t.wrongSoft,
                borderRadius: t.radius.md,
              }}
            >
              {error}
            </div>
          )}
        </div>
      ),
    });
  }

  // ---------------------------------------------------------------------------
  // Render: State 2 — Running
  // ---------------------------------------------------------------------------

  if (state.phase === 'running') {
    const q = state.questions[currentIndex];
    const hasAnswer = q ? answers.has(q.id) : false;

    return renderShell({
      title: 'Simula Esame',
      subtitle: `${state.mode.charAt(0).toUpperCase() + state.mode.slice(1)}`,
      timer: timerDisplay,
      timerState: timerUrgent ? 'urgent' : timerAdvisory ? 'advisory' : 'normal',
      children: (
        <>
        {/* V28: compact one-line progress strip (simula-exam.html L33-37) —
            a thin gradient-filled bar + a tabular "Domanda N di M" counter on
            the same row. */}
        <div
          style={{
            padding:    '8px 16px 0',
            flexShrink: 0,
            display:    'flex',
            alignItems: 'center',
            gap:        10,
          }}
        >
          <div
            style={{
              flex:         1,
              height:       6,
              background:   t.line,
              borderRadius: 999,
              overflow:     'hidden',
            }}
            aria-hidden
          >
            <div
              style={{
                height:       '100%',
                width:        `${((currentIndex + 1) / state.questions.length) * 100}%`,
                background:   t.brandGradient,
                borderRadius: 999,
                transition:   'width 0.2s ease',
              }}
            />
          </div>
          <span
            style={{
              fontFamily:         t.fonts.body ?? t.fonts.sans,
              fontSize:           12,
              fontWeight:         700,
              color:              t.textMuted,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace:         'nowrap',
            }}
          >
            Domanda {currentIndex + 1} di {state.questions.length}
          </span>
        </div>

        {/* Timer urgency banner — amber at ≤5min, rose at ≤1min. Spec §5.5:
            color shift only, NEVER blinking (anxiety / motion-sensitivity);
            urgency is announced via aria-live. */}
        {(timerUrgent || timerAdvisory) && (
          <div
            style={{
              margin:       '8px 16px 0',
              padding:      '6px 12px',
              background:   timerUrgent ? t.wrongSoft : t.accentSoft,
              borderRadius: t.radius.md,
              fontFamily:   t.fonts.mono,
              fontSize:     13,
              color:        timerUrgent ? t.wrong : t.accentInk,
              fontWeight:   600,
            }}
            role="status"
            aria-live={timerUrgent ? 'assertive' : 'polite'}
          >
            {timerUrgent
              ? `Ultimo minuto! ${timerDisplay} rimanenti`
              : `Ti restano ${Math.ceil(secondsLeft / 60)} minuti`}
          </div>
        )}

        {/* Question area */}
        {/* V29 S6 — RULE 2.3 (founder, 2026-07-11): VERO/FALSO are ALWAYS
            visible without scrolling. The QUESTION area (eyebrow + card)
            compresses/scrolls within its own region; the answer pills live in
            a pinned block below it, never inside the scrolling card. */}
        <div
          style={{
            flex:     1,
            minHeight: 0,
            overflow: 'auto',
            padding:  '12px 16px 4px',
            display:  'flex',
            flexDirection: 'column',
            gap:      12,
          }}
        >
          {q && (
            <>
              <div
                style={{
                  fontFamily:    t.fonts.body ?? t.fonts.sans,
                  fontSize:      11,
                  fontWeight:    700,
                  color:         t.textFaint,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                Domanda {currentIndex + 1} · {q.argomenti?.name_it ?? 'Generale'}
              </div>

              {/* key={q.id} — founder phone bug (2026-07-08): without it React
                  reuses the SAME card DOM across questions, and any
                  imperatively-set hover style from the previous tap survives
                  the props diff → the next question renders pre-highlighted.
                  Remounting per question makes leakage impossible. */}
              <QCard
                key={q.id}
                text={q.text_it}
                imageRef={q.image_ref ?? null}
                imageAlt={`Segnale — ${q.argomenti?.name_it ?? 'segnaletica'}`}
                medialess={!q.image_ref}
              />
            </>
          )}
        </div>

        {/* Pinned answer block — above the footer, outside every scroll. */}
        {q && (() => {
          const answeredRec = answers.get(q.id);
          const answeredChoice = answeredRec?.answer ?? null;
          return (
            <div
              data-testid="simula-answer-block"
              style={{
                flexShrink: 0,
                padding: '8px 16px 4px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {/* Guidata feedback verdict — sage/rose, icon-backed via the
                  semantic tokens; color-only is never the sole signal. */}
              {guidataFeedback && (
                <div
                  role="status"
                  style={{
                    alignSelf:    'center',
                    display:      'inline-flex',
                    alignItems:   'center',
                    gap:          6,
                    padding:      '4px 12px',
                    borderRadius: t.radius.pill,
                    background:   guidataFeedback === 'right' ? t.rightSoft : t.wrongSoft,
                    color:        guidataFeedback === 'right' ? t.rightText : t.wrongText,
                    fontFamily:   t.fonts.body ?? t.fonts.sans,
                    fontSize:     13,
                    fontWeight:   700,
                  }}
                >
                  <span aria-hidden>{guidataFeedback === 'right' ? '✓' : '✕'}</span>
                  {guidataFeedback === 'right' ? 'Giusto!' : 'Sbagliato'}
                </div>
              )}
              {/* V29 S6: locked answers say WHY they're locked ("risposta
                  consegnata") instead of silently ignoring taps. */}
              {hasAnswer && !guidataFeedback && (
                <p style={{ alignSelf: 'center', margin: 0, fontSize: 11.5, fontWeight: 600, color: t.textFaint, fontFamily: t.fonts.body ?? t.fonts.sans }}>
                  Risposta consegnata — non si può cambiare, come all'esame.
                </p>
              )}
              <VeroFalsoPills
                key={q.id}
                selected={answeredChoice}
                disabled={hasAnswer || guidataFeedback !== null}
                onPick={(choice) => handleAnswer(choice)}
              />
              <p
                style={{
                  textAlign:  'center',
                  fontSize:   11.5,
                  color:      t.textFaint,
                  fontFamily: t.fonts.body ?? t.fonts.sans,
                  margin:     0,
                }}
              >
                {state.mode === 'guidata'
                  ? 'Vedi subito se hai risposto giusto dopo ogni domanda.'
                  : 'Nessun aiuto durante la prova. Vedi il risultato solo alla fine.'}
              </p>
            </div>
          );
        })()}

        {/* V28 composer-style exam footer (simula-exam.html L74-87): a prev
            icon button + a single primary "Avanti" button. KNOWN-OUTDATED #2
            (no Avanti on LESSONS) does NOT apply to the exam — it is
            intentionally button-driven. "Tutte" (the 30-cell navigator) is a
            line-style entry on the LEFT next to prev; it keeps the
            `simula-open-navigator` testid + dialog semantics. The early-
            finalize "Consegna ora" lives inside the navigator (standalone). */}
        <div
          style={{
            padding:        '10px 16px max(16px, var(--sai-bottom))',
            flexShrink:     0,
            display:        'flex',
            alignItems:     'center',
            gap:            10,
            borderTop:      `1px solid ${t.line}`,
            background:     t.mode === 'dark' ? 'rgba(22,16,31,0.55)' : 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
          }}
        >
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            aria-label="Domanda precedente"
            title="Precedente"
            style={{
              width:        44,
              height:       44,
              flexShrink:   0,
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
              background:   'transparent',
              border:       `1px solid ${t.lineStrong}`,
              borderRadius: t.radius.md,
              color:        currentIndex === 0 ? t.textFaint : t.text,
              cursor:       currentIndex === 0 ? 'not-allowed' : 'pointer',
              opacity:      currentIndex === 0 ? 0.5 : 1,
            }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          {/* "Tutte" — opens the 30-cell navigator overlay. */}
          <button
            data-testid="simula-open-navigator"
            onClick={() => setNavOpen(true)}
            aria-haspopup="dialog"
            style={{
              height:       44,
              flexShrink:   0,
              display:      'flex',
              alignItems:   'center',
              gap:          6,
              padding:      '0 14px',
              background:   'transparent',
              border:       `1px solid ${t.lineStrong}`,
              borderRadius: t.radius.md,
              color:        t.text,
              fontFamily:   t.fonts.body ?? t.fonts.sans,
              fontSize:     13,
              fontWeight:   600,
              cursor:       'pointer',
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
            </svg>
            Tutte ({answers.size}/{state.questions.length})
          </button>
          <button
            onClick={handleSkip}
            disabled={currentIndex >= state.questions.length - 1}
            style={{
              flex:         1,
              minHeight:    44,
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
              gap:          8,
              background:   currentIndex >= state.questions.length - 1 ? t.line : t.accent,
              border:       'none',
              borderRadius: t.radius.md,
              color:        currentIndex >= state.questions.length - 1 ? t.textFaint : (t.mode === 'dark' ? '#1a1510' : '#fff'),
              fontFamily:   t.fonts.body ?? t.fonts.sans,
              fontSize:     15,
              fontWeight:   700,
              cursor:       currentIndex >= state.questions.length - 1 ? 'not-allowed' : 'pointer',
            }}
            aria-label="Domanda successiva"
          >
            Avanti
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>

        {/* In-exam navigator overlay (spec §5.5: answered ✓ / current / to-do). */}
        {navOpen && (
          <QuestionNavigator
            questions={state.questions}
            answers={answers}
            currentIndex={currentIndex}
            t={t}
            onJump={(i) => {
              currentIndexRef.current = i;
              setCurrentIndex(i);
              setGuidataFeedback(null);
              setNavOpen(false);
            }}
            onClose={() => setNavOpen(false)}
            onConsegna={() => {
              const answered = answers.size;
              const total = state.questions.length;
              if (answered !== SIMULA_QUESTION_COUNT || total !== SIMULA_QUESTION_COUNT) return;
              setNavOpen(false);
              setConfirmAction({
                title: 'Consegnare la simulazione?',
                body: 'Hai risposto a tutte le domande. Vedrai subito il risultato.',
                confirmLabel: 'Consegna',
                onConfirm: () => {
                  setConfirmAction(null);
                  void finalize(
                    state.simulationId,
                    state.mode,
                    state.questions,
                    latestAnswersRef.current,
                    state.startedAt,
                    state.deadlineAt,
                  );
                },
              });
            }}
          />
        )}
        <ConfirmModal
          open={confirmAction !== null}
          title={confirmAction?.title ?? ''}
          body={confirmAction?.body}
          confirmLabel={confirmAction?.confirmLabel}
          destructive={confirmAction?.destructive}
          onConfirm={() => confirmAction?.onConfirm()}
          onCancel={() => setConfirmAction(null)}
        />
        </>
      ),
    });
  }

  // ---------------------------------------------------------------------------
  // Render: State 3 — Review (walk through wrong answers with Prof explanations)
  // ---------------------------------------------------------------------------

  if (state.phase === 'review') {
    return (
      <ReviewView
        state={state}
        onBack={handleReviewBack}
        onNav={(delta) => setState({ ...state, currentReviewIdx: Math.max(0, Math.min(state.wrongIndices.length - 1, state.currentReviewIdx + delta)) })}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Render: State 4 — Post-mortem
  // ---------------------------------------------------------------------------

  // V28 result hero (simula-result.html L16-28): a check/cross avatar, the big
  // verdict word, an error-count-vs-limit subline ("N errori su 30 ·
  // sotto/oltre il limite di 3"), and a corrette cluster. Verdict vocabulary is
  // kept COHERENT with the already-V28 SimulaPostMortemScreen (PERFETTO /
  // PROMOSSO / "Ci sei quasi") so the same Simula reads identically live and
  // from Cronologia. Probability moves OUT of the hero into a Regola-style
  // nudge at the bottom.
  const { breakdown } = state;
  const totalQuestions = state.questions.length;
  const score = Math.max(0, Math.min(totalQuestions, Math.floor(state.score)));
  const errors = Math.max(0, totalQuestions - score);
  const PASS_THRESHOLD_ERRORS = 3; // soglia 27/30 → max 3 errori
  const perfetto = score === totalQuestions;
  const passed = state.passed;
  // A shortened/defensive payload cannot be declared an official pass by the
  // 30-question backend rule. A genuinely perfect partial result still needs
  // one coherent positive presentation, without changing the stored verdict.
  const nonStandardPerfect = totalQuestions !== 30 && perfetto;
  const positivePresentation = passed || nonStandardPerfect;
  // FP10-M4 — extend the V28 3-outcome verdict with an honest LOW tier. At
  // 8/30 (22 errori) "Ci sei quasi" read as near-success — condescending and
  // false. When the score is far below the soglia (score < 20, i.e. >10
  // errori) the headline is honest-but-warm Il Prof copy instead, without
  // humiliation. Deliberate extension of the locked 3-outcome contract, driven
  // by live evidence (founder can veto — see commit body).
  const LOW_SCORE_CEILING = 20; // score < 20 (errori > 10) → "Da costruire"
  const lowScore = !positivePresentation && score < LOW_SCORE_CEILING;
  const passColor = positivePresentation ? t.right : t.wrong;
  const passText = positivePresentation ? t.rightText : t.wrongText;
  const passBackground = positivePresentation ? t.rightSoft : t.wrongSoft;
  const verdictWord = perfetto
    ? 'Perfetto!'
    : passed
      ? 'PROMOSSO'
      : lowScore
        ? 'Da costruire'
        : 'Ci sei quasi';
  const heroSubline = totalQuestions !== 30
    ? `${errors} ${errors === 1 ? 'errore' : 'errori'} su ${totalQuestions} · simulazione non standard`
    : errors <= PASS_THRESHOLD_ERRORS
      ? `${errors} ${errors === 1 ? 'errore' : 'errori'} su ${totalQuestions} · sotto il limite di ${PASS_THRESHOLD_ERRORS}`
      : lowScore
        ? `${errors} errori su ${totalQuestions} · partiamo dalle basi, un passo alla volta`
        : `${errors} errori su ${totalQuestions} · oltre il limite di ${PASS_THRESHOLD_ERRORS}`;
  // Exam probability (cieca only, ≥5 blind sims). Stored 0..1 → show as %.
  const probPct = state.probability != null ? Math.round(state.probability * 100) : null;
  const probInfo = probPct != null ? probabilityBand(probPct) : null;
  const probColor = probInfo ? (t.mode === 'dark' ? probInfo.colorDark : probInfo.color) : t.textMuted;

  return renderShell({
    title: 'Simula Esame',
    subtitle: 'Esito della simulazione',
    children: (
      <div
        style={{
          flex:     1,
          overflow: 'auto',
          padding:  '16px 16px 24px',
          display:  'flex',
          flexDirection: 'column',
          gap:      16,
        }}
      >
        {(state.durability === 'result_saved_mastery_deferred' || state.masteryStatus === 'legacy_unverified') && (
          <div
            role="status"
            data-testid="simula-mastery-deferred"
            style={{
              padding: '10px 12px',
              background: t.accentSoft,
              border: `1px solid ${t.accent}`,
              borderRadius: t.radius.md,
              color: t.text,
              fontFamily: t.fonts.body ?? t.fonts.sans,
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.45,
            }}
          >
            Risultato salvato. Non è stato possibile verificare l’aggiornamento dei progressi.
          </div>
        )}
        {/* Verdict hero — centered avatar + big word + error-count subline. */}
        <div
          style={{
            background:   passBackground,
            border:       `1px solid ${passColor}`,
            borderRadius: t.radius.xl,
            padding:      '24px 20px',
            display:      'flex',
            flexDirection:'column',
            alignItems:   'center',
            textAlign:    'center',
            gap:          8,
          }}
        >
          <span
            aria-hidden
            style={{
              width:        56,
              height:       56,
              borderRadius: 16,
              background:   passColor,
              boxShadow:    `0 3px 10px ${positivePresentation ? 'rgba(46,141,111,0.3)' : 'rgba(197,54,91,0.3)'}`,
              display:      'flex',
              alignItems:   'center',
              justifyContent:'center',
            }}
          >
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              {positivePresentation ? <polyline points="20 6 9 17 4 12" /> : <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>}
            </svg>
          </span>
          <div style={{ fontSize: 28, fontWeight: 800, color: passText, letterSpacing: 0.5 }}>
            {verdictWord}
          </div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: passText }}>
            {heroSubline}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: t.textMuted, fontSize: 12.5, fontWeight: 600 }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{score}/{totalQuestions} corrette</span>
          </div>
          {/* Exam probability nudge — Regola-style, bound to the bottom of the
              result (simula-result.html L52-55), NOT the hero figure. */}
          {probPct != null && (
            <div
              data-testid="simula-probability"
              style={{
                marginTop:    10,
                alignSelf:    'stretch',
                display:      'flex',
                alignItems:   'center',
                gap:          8,
                padding:      '8px 12px',
                borderRadius: t.radius.md,
                background:   t.surface,
                border:       `1px solid ${t.line}`,
                fontFamily:   t.fonts.body ?? t.fonts.sans,
                fontSize:     13,
                color:        t.text,
                textAlign:    'left',
              }}
            >
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: probColor, flexShrink: 0 }} />
              <span>
                Stima prudente di superare l’esame:{' '}
                <strong style={{ color: probColor }}>{probPct}%</strong>
                {probInfo && <span style={{ color: t.textMuted }}> ({probInfo.label.toLowerCase()})</span>}
                <span style={{ color: t.textMuted }}>. Non è una garanzia.</span>
              </span>
            </div>
          )}
          {/* V29 S6: when the probability isn't unlocked yet, SAY SO (V28
              contract) — silence read as a bug. Cieca sims count toward it. */}
          {probPct == null && state.mode === 'cieca' && (
            <div
              data-testid="simula-probability-locked"
              style={{
                marginTop:    10,
                alignSelf:    'stretch',
                padding:      '8px 12px',
                borderRadius: t.radius.md,
                background:   t.surface,
                border:       `1px dashed ${t.lineStrong}`,
                fontFamily:   t.fonts.body ?? t.fonts.sans,
                fontSize:     12.5,
                color:        t.textMuted,
                textAlign:    'left',
              }}
            >
              Stima prudente di superare l’esame: si sblocca con almeno 5 simulazioni complete in modalità Cieca
              negli ultimi 14 giorni, di cui una negli ultimi 3 — questa conta.
            </div>
          )}
        </div>

        {/* RESPINTO routes the student to work the weak topics. Sits BELOW the
            clean verdict hero (the hero stays prototype-faithful). */}
        {!positivePresentation && (
          <button
            onClick={() => { resetForDrawerTarget('progresso'); navigate('/progresso'); }}
            style={{
              alignSelf:    'stretch',
              minHeight:    44,
              display:      'flex',
              alignItems:   'center',
              justifyContent:'center',
              gap:          8,
              background:   t.wrong,
              color:        t.mode === 'dark' ? '#1a1510' : '#fff',
              border:       'none',
              borderRadius: t.radius.md,
              padding:      '12px 18px',
              fontFamily:   t.fonts.body ?? t.fonts.sans,
              fontSize:     14,
              fontWeight:   700,
              cursor:       'pointer',
            }}
          >
            Lavoriamo sugli argomenti deboli →
          </button>
        )}

        {/* V28 "Dove hai sbagliato" (simula-result.html L31-49): only the
            argomenti WITH errors, each row = rose error-count tag + name +
            "N errori" + chevron, tappable → jump to that argomento's first
            wrong answer in the review. */}
        {breakdown.some((r) => r.correct < r.total) && (
          <div
            style={{
              background:   t.surface,
              border:       `1px solid ${t.line}`,
              borderRadius: t.radius.lg,
              padding:      '12px 16px',
            }}
          >
            <div
              style={{
                fontFamily:  t.fonts.body ?? t.fonts.sans,
                fontSize:    11,
                fontWeight:  700,
                color:       t.textFaint,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              Dove hai sbagliato
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {breakdown
                .filter((row) => row.correct < row.total)
                .map((row) => {
                  const rowErrors = row.total - row.correct;
                  return (
                    <button
                      key={row.argomentoName}
                      onClick={() => handleStartReview(row.argomentoName)}
                      aria-label={`Rivedi gli errori di ${row.argomentoName} (${row.correct} su ${row.total})`}
                      style={{
                        width:          '100%',
                        display:        'flex',
                        alignItems:     'center',
                        gap:            10,
                        padding:        '10px 0',
                        minHeight:      44,
                        borderBottom:   `1px solid ${t.line}`,
                        background:     'transparent',
                        border:         'none',
                        textAlign:      'left',
                        cursor:         'pointer',
                        fontFamily:     t.fonts.body ?? t.fonts.sans,
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          flexShrink:    0,
                          minWidth:      28,
                          height:        28,
                          padding:       '0 6px',
                          borderRadius:  8,
                          background:    t.wrongSoft,
                          color:         t.wrongText,
                          border:        `1px solid rgba(197,54,91,0.45)`,
                          fontSize:      13,
                          fontWeight:    700,
                          fontVariantNumeric: 'tabular-nums',
                          display:       'flex',
                          alignItems:    'center',
                          justifyContent:'center',
                        }}
                      >
                        {rowErrors}
                      </span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: t.text }}>
                        {row.argomentoName}
                      </span>
                      <span style={{ flexShrink: 0, fontSize: 12.5, fontWeight: 700, color: t.textMuted }}>
                        {rowErrors} {rowErrors === 1 ? 'errore' : 'errori'}
                      </span>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={t.textFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {/* Pedagogical suggestion: weakest argomento → Chiedi handoff */}
        {(() => {
          if (positivePresentation) return null;
          // Find the argomento with the worst ratio AND at least one wrong answer.
          const weakest = [...breakdown]
            .filter((r) => r.correct < r.total)
            .sort((a, b) => a.correct / a.total - b.correct / b.total)[0];
          if (!weakest) return null;
          const prefill = `Il mio argomento più debole è "${weakest.argomentoName}" (ho fatto ${weakest.correct} su ${weakest.total}). Puoi aiutarmi a ripassarlo? Spiegami i punti chiave e dimmi a cosa devo stare più attento.`;
          return (
            <div
              style={{
                background:   t.accentSoft,
                border:       `1px solid ${t.accent}`,
                borderRadius: t.radius.lg,
                padding:      '14px 16px',
              }}
            >
              <div style={{ fontFamily: t.fonts.mono, fontSize: 10, fontWeight: 600, color: t.accentInk, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6 }}>
                Argomento più debole
              </div>
              <p style={{ fontFamily: t.fonts.body, fontSize: 14, color: t.text, margin: '0 0 10px', lineHeight: 1.5 }}>
                <strong>{weakest.argomentoName}</strong>: {weakest.correct}/{weakest.total}. Vuoi che ti aiuti a ripassarlo con il Prof?
              </p>
              <button
                onClick={() => navigate('/prof', { state: { prefill, autoSend: true } })}
                style={{
                  background:    t.accent,
                  color:         t.mode === 'dark' ? '#1a1510' : '#fff',
                  border:        'none',
                  borderRadius:  t.radius.pill,
                  padding:       '9px 16px',
                  fontFamily:    t.fonts.body,
                  fontSize:      13,
                  fontWeight:    600,
                  cursor:        'pointer',
                }}
              >
                Ripassa con il Prof →
              </button>
            </div>
          );
        })()}

        {/* Transient toast for clipboard feedback — aria-live so screen
            readers announce the copy confirmation. */}
        {shareToast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              alignSelf:     'flex-start',
              margin:        '0 0 8px',
              padding:       '6px 12px',
              background:    t.accentSoft,
              border:        `1px solid ${t.accent}`,
              borderRadius:  t.radius.pill,
              fontFamily:    t.fonts.body,
              fontSize:      12.5,
              color:         t.text,
            }}
          >
            {shareToast}
          </div>
        )}

        {/* Action chips. Spec §5.5 "Perfetto" (30/30): NO Rivedi button — only
            Condividi + Nuova simulazione. Below 30 the Rivedi chip is shown. */}
        <ActionChips
          actions={[
            ...(errors > 0 ? [{
              label:   `Rivedi gli errori (${errors})`,
              // B2 hygiene (V29 S2): ActionChips passes the handler straight to
              // the DOM onClick, so a bare reference would receive the
              // MouseEvent as `argomentoName` (truthy object → findIndex miss →
              // silent index-0 fallback). Call with no args explicitly.
              onClick: () => handleStartReview(),
              title:   'Rivedi ogni risposta sbagliata con la spiegazione del Prof.',
            }] : []),
            {
              label:   'Condividi il risultato',
              onClick: handleShareScore,
              title:   'Copia il tuo risultato negli appunti o apri il menu Condividi.',
            },
            {
              kind:    'primary',
              label:   'Nuova simulazione',
              onClick: () => handleReset(),
            },
          ]}
        />
      </div>
    ),
  });
}

// ---------------------------------------------------------------------------
// QuestionNavigator — in-exam "Tutte" overlay (spec §5.5). A 30-cell grid:
// answered (filled accent + ✓) / current (outlined ring) / to-do (faint).
// Color is never the only signal — each state has a distinct shape/glyph.
// Cells are ≥44px; the grid reflows (6–10 per row by width). Optional
// "Consegna ora" early-finalize (standalone only).
// ---------------------------------------------------------------------------

interface QuestionNavigatorProps {
  questions: QuestionDto[];
  answers: Map<string, AnswerRecord>;
  currentIndex: number;
  t: ReturnType<typeof useTokens>;
  onJump: (index: number) => void;
  onClose: () => void;
  onConsegna?: () => void;
}

function QuestionNavigator({
  questions,
  answers,
  currentIndex,
  t,
  onJump,
  onClose,
  onConsegna,
}: QuestionNavigatorProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    // Move focus into the dialog on open so keyboard/AT users aren't left
    // behind the backdrop on the now-obscured exam controls (a11y).
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const missingIndices = questions
    .map((question, index) => (answers.has(question.id) ? -1 : index))
    .filter((index) => index >= 0);
  const canSubmit = missingIndices.length === 0 && hasExactAnswerSet(questions, answers);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Vai a una domanda"
      data-testid="simula-navigator"
      onClick={onClose}
      style={{
        position:       'fixed',
        inset:          0,
        background:     'rgba(0,0,0,0.6)',
        display:        'flex',
        alignItems:     'flex-end',
        justifyContent: 'center',
        paddingBottom:  `calc(${TABBAR_H}px + var(--sai-bottom))`,
        boxSizing:      'border-box',
        zIndex:         1000,
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          background:    t.bg,
          borderTopLeftRadius:  t.radius.xl,
          borderTopRightRadius: t.radius.xl,
          border:        `1px solid ${t.line}`,
          padding:       '16px 16px 24px',
          width:         '100%',
          maxWidth:      460,
          maxHeight:     '80vh',
          overflowY:     'auto',
          boxSizing:     'border-box',
          outline:       'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontFamily: t.fonts.serif, fontSize: 17, fontWeight: 700, color: t.text }}>
            Tutte le domande
          </span>
          <span style={{ fontFamily: t.fonts.body ?? t.fonts.sans, fontSize: 13, color: t.textMuted }}>
            {answers.size}/{questions.length} risposte
          </span>
        </div>

        <div
          style={{
            display:             'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
            gap:                 8,
          }}
        >
          {questions.map((q, i) => {
            const answered = answers.has(q.id);
            const current = i === currentIndex;
            const bg = answered ? t.accent : current ? 'transparent' : t.surface2;
            const fg = answered ? (t.mode === 'dark' ? '#1a1510' : '#fff') : current ? t.accentInk : t.textMuted;
            const border = current ? `2px solid ${t.accent}` : `1px solid ${t.line}`;
            return (
              <button
                key={q.id}
                onClick={() => onJump(i)}
                aria-label={
                  `Domanda ${i + 1}` +
                  (answered ? ' — risposta data' : current ? ' — corrente' : ' — da fare')
                }
                aria-current={current ? 'true' : undefined}
                style={{
                  position:     'relative',
                  minWidth:     44,
                  minHeight:    44,
                  background:   bg,
                  border,
                  borderRadius: t.radius.md,
                  color:        fg,
                  fontFamily:   t.fonts.mono,
                  fontSize:     14,
                  fontWeight:   current ? 700 : 600,
                  cursor:       'pointer',
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                }}
              >
                {i + 1}
                {answered && (
                  <span
                    aria-hidden
                    style={{
                      position:  'absolute',
                      top:       2,
                      right:     4,
                      fontSize:  9,
                      lineHeight: 1,
                      color:     fg,
                    }}
                  >
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              flex:         1,
              minHeight:    44,
              background:   'transparent',
              border:       `1px solid ${t.lineStrong}`,
              borderRadius: t.radius.pill,
              color:        t.text,
              fontFamily:   t.fonts.body ?? t.fonts.sans,
              fontSize:     14,
              fontWeight:   600,
              cursor:       'pointer',
            }}
          >
            Chiudi
          </button>
          {onConsegna && (
            <button
              data-testid="simula-consegna"
              onClick={onConsegna}
              disabled={!canSubmit}
              style={{
                flex:         1,
                minHeight:    44,
                background:   canSubmit ? t.accent : t.line,
                border:       'none',
                borderRadius: t.radius.pill,
                color:        canSubmit ? (t.mode === 'dark' ? '#1a1510' : '#fff') : t.textFaint,
                fontFamily:   t.fonts.body ?? t.fonts.sans,
                fontSize:     14,
                fontWeight:   700,
                cursor:       canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              Consegna
            </button>
          )}
        </div>
        {/* R1: incomplete attempts cannot be submitted; route the student to
            the first missing answer instead of silently recording errors. */}
        {onConsegna && !canSubmit && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <p
            data-testid="simula-consegna-warning"
            role="status"
            style={{
              margin:     0,
              textAlign:  'center',
              fontFamily: t.fonts.body ?? t.fonts.sans,
              fontSize:   11.5,
              color:      t.textFaint,
            }}
          >
            {missingIndices.length === 1
              ? 'Hai 1 domanda senza risposta.'
              : `Hai ${missingIndices.length} domande senza risposta.`}
          </p>
          <button
            onClick={() => onJump(missingIndices[0])}
            style={{
              minHeight: 44,
              padding: '8px 14px',
              border: `1px solid ${t.accent}`,
              borderRadius: t.radius.pill,
              background: 'transparent',
              color: t.accentInk,
              fontFamily: t.fonts.body ?? t.fonts.sans,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Vai alla prima domanda senza risposta
          </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReviewView — shown when the user clicks "Rivedi gli errori" on the
// post-mortem. Walks through each wrong answer with question, image, the
// student's answer vs correct answer, and a streamed Prof explanation.
// Explanations are cached in zustand (per-questionId thread) so navigating
// back doesn't re-stream.
// ---------------------------------------------------------------------------

interface ReviewViewProps {
  state: Extract<SimulaState, { phase: 'review' }>;
  onBack: () => void;
  onNav: (delta: -1 | 1) => void;
}

function ReviewView({ state, onBack, onNav }: ReviewViewProps): JSX.Element {
  const t = useTokens();

  const wrongIdx = state.wrongIndices[state.currentReviewIdx];
  const q = state.questions[wrongIdx];
  const answerRec = state.answers.get(q.id);
  const studentAnswerBool = answerRec ? answerRec.answer === 'Vero' : null;
  const studentAnswered = studentAnswerBool !== null;
  const correctBool = q.is_true;

  // Stable thread-id per question so explanations cache across nav + refresh.
  const threadId = `simula-review-${q.id}`;
  const messages = useChat((s) => s.threads[threadId]?.messages ?? []);
  const isStreaming = useChat((s) => s.threads[threadId]?.streaming ?? false);
  const expandedPrinciple = useChat((s) => s.expandedPrinciple);
  const [resolvedPrinciple, setResolvedPrinciple] = useState<Principle | null>(null);

  // Trigger streaming on mount / when index changes, but only if this thread
  // has no content yet (navigation-back caches).
  useEffect(() => {
    const existing = useChat.getState().threads[threadId];
    if (existing && existing.messages.length > 0) return;
    // Fire stream
    if (!studentAnswered) {
      // Unanswered wrong. Fake the student answer as opposite of correct so the
      // explanation addresses why the correct answer is what it is.
      runExplanationStream(threadId, q.id, !correctBool);
    } else {
      runExplanationStream(threadId, q.id, studentAnswerBool as boolean);
    }
  }, [threadId, studentAnswered, correctBool, q.id, studentAnswerBool]);

  async function handleExpandPrinciple(messageId: string, principleId: string) {
    useChat.getState().expandPrinciple(messageId, principleId);
    try {
      const principle = await kbApi.getPrinciple(principleId);
      setResolvedPrinciple({
        id:            principle.id,
        argomentoName: principle.argomenti?.name_it ?? '',
        nameIt:        principle.name_it,
        enunciato:     principle.enunciato,
        regola:        principle.regola,
      });
    } catch {
      /* no-op */
    }
  }

  function handleCollapse() {
    useChat.getState().collapsePrinciple();
    setResolvedPrinciple(null);
  }

  const atFirst = state.currentReviewIdx === 0;
  const atLast = state.currentReviewIdx === state.wrongIndices.length - 1;

  const reviewBody = (
    <>
      <div
        style={{
          flex:          1,
          overflow:      'auto',
          padding:       '12px 16px 8px',
          display:       'flex',
          flexDirection: 'column',
          gap:           12,
        }}
      >
        {/* Question card — shows sign image + text + argomento */}
        <div
          style={{
            background:   t.surface,
            border:       `1px solid ${t.line}`,
            borderRadius: t.radius.lg,
            padding:      '14px 16px',
          }}
        >
          <div
            style={{
              fontFamily:    t.fonts.body ?? t.fonts.sans,
              fontSize:      10,
              fontWeight:    600,
              color:         t.textFaint,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              marginBottom:  8,
            }}
          >
            {q.argomenti?.name_it ?? 'Generale'}
          </div>

          {q.image_ref && (
            <div style={{ marginBottom: 10, maxWidth: 220, minHeight: 120, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={`/${q.image_ref}`}
                alt={`Segnale — ${q.argomenti?.name_it ?? 'segnaletica'}`}
                style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                decoding="async"
              />
            </div>
          )}

          <p
            style={{
              fontFamily:  t.fonts.serif,
              fontSize:    16,
              color:       t.text,
              lineHeight:  1.5,
              margin:      0,
            }}
          >
            {q.text_it}
          </p>

          {/* Answer comparison strip */}
          <div
            style={{
              display:      'flex',
              gap:          8,
              marginTop:    14,
              alignItems:   'stretch',
            }}
          >
            <div
              style={{
                flex:         1,
                padding:      '8px 12px',
                background:   t.wrongSoft,
                border:       `1px solid ${t.wrong}`,
                borderRadius: t.radius.md,
              }}
            >
              <div style={{ fontSize: 10, fontFamily: t.fonts.mono, color: t.wrongText, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>
                Tua risposta
              </div>
              <div style={{ fontFamily: t.fonts.body, fontSize: 14, fontWeight: 600, color: t.text }}>
                {studentAnswered ? (studentAnswerBool ? 'Vero' : 'Falso') : 'Non data'}
              </div>
            </div>
            <div
              style={{
                flex:         1,
                padding:      '8px 12px',
                background:   t.rightSoft,
                border:       `1px solid ${t.right}`,
                borderRadius: t.radius.md,
              }}
            >
              <div style={{ fontSize: 10, fontFamily: t.fonts.mono, color: t.rightText, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>
                Corretta
              </div>
              <div style={{ fontFamily: t.fonts.body, fontSize: 14, fontWeight: 600, color: t.text }}>
                {correctBool ? 'Vero' : 'Falso'}
              </div>
            </div>
          </div>
        </div>

        {/* Prof's explanation — reuse ChatThread for ProfProse + citation chips */}
        {messages.length > 0 ? (
          <ChatThread
            messages={messages}
            expandedPrinciple={expandedPrinciple}
            onExpandPrinciple={handleExpandPrinciple}
            onCollapsePrinciple={handleCollapse}
            resolvedPrinciple={resolvedPrinciple}
          />
        ) : (
          <div style={{ padding: '16px', color: t.textMuted, fontSize: 14, fontFamily: t.fonts.body, textAlign: 'center' }}>
            Il Prof sta preparando la spiegazione…
          </div>
        )}
      </div>

      {/* Navigation bar */}
      <div
        style={{
          padding:    '0 16px 16px',
          flexShrink: 0,
          display:    'flex',
          gap:        8,
          alignItems: 'center',
        }}
      >
        <button
          onClick={() => onNav(-1)}
          disabled={atFirst || isStreaming}
          style={{
            padding:      '10px 14px',
            background:   t.surface,
            border:       `1px solid ${t.lineStrong}`,
            borderRadius: t.radius.pill,
            color:        atFirst || isStreaming ? t.textFaint : t.text,
            cursor:       atFirst || isStreaming ? 'not-allowed' : 'pointer',
            fontFamily:   t.fonts.body,
            fontSize:     13,
            opacity:      atFirst || isStreaming ? 0.55 : 1,
          }}
          aria-label="Errore precedente"
        >
          ← Precedente
        </button>
        <button
          onClick={onBack}
          style={{
            padding:      '10px 14px',
            background:   'transparent',
            border:       'none',
            color:        t.textMuted,
            cursor:       'pointer',
            fontFamily:   t.fonts.body,
            fontSize:     13,
          }}
        >
          Al risultato
        </button>
        <button
          // B1 fix (V29 S2): on the LAST error the button used to keep the
          // "Fine ripasso" label while disabled — a dead-end CTA. It now stays
          // ACTIVE and closes the loop back to the esito (approved design:
          // ultimo errore → Fine ripasso → risultato). Mid-list it advances.
          onClick={() => { if (atLast) onBack(); else onNav(1); }}
          disabled={isStreaming}
          style={{
            flex:         1,
            padding:      '10px 14px',
            background:   isStreaming ? t.surface : t.accent,
            color:        isStreaming ? t.textFaint : (t.mode === 'dark' ? '#1a1510' : '#fff'),
            border:       isStreaming ? `1px solid ${t.lineStrong}` : 'none',
            borderRadius: t.radius.pill,
            fontFamily:   t.fonts.body,
            fontSize:     13,
            fontWeight:   600,
            cursor:       isStreaming ? 'not-allowed' : 'pointer',
            opacity:      isStreaming ? 0.55 : 1,
          }}
          aria-label={atLast ? 'Fine ripasso — torna al risultato' : 'Errore successivo'}
        >
          {atLast ? 'Fine ripasso' : 'Successivo →'}
        </button>
      </div>
    </>
  );

  return (
    <Screen>
      <TopChrome
        title={`Errore ${state.currentReviewIdx + 1} di ${state.wrongIndices.length}`}
        subtitle="Ripasso"
        mode="simula"
        // V29 S1: review is a deep phase — back returns to the esito.
        onBack={onBack}
      />
      {reviewBody}
    </Screen>
  );
}

/**
 * Kick off a `simula_review` explanation stream into the given chat thread.
 * Streaming hygiene (inherited from the retired PracticeScreen) — placeholder message,
 * token-by-token append, replace_text swap at end, removeStreamingPlaceholder
 * on refuse/error so no stuck "Il Prof sta scrivendo…".
 */
function runExplanationStream(threadId: string, questionId: string, studentAnswerBool: boolean): void {
  useChat.getState().startThread(threadId);
  useChat.getState().appendMessage(threadId, {
    id: crypto.randomUUID(),
    authorKind: 'prof',
    kind: 'streaming',
    text: '',
  });
  useChat.getState().setStreaming(threadId, true);
  void explainWrongAnswerStream(
    {
      question_id: questionId,
      student_answer: studentAnswerBool,
      mode: 'simula_review',
    },
    {
      onToken: (chunk) => useChat.getState().appendToLastMessage(threadId, chunk),
      onReplaceText: (fullText) => useChat.getState().replaceLastMessageText(threadId, fullText),
      onCitations: (cites) => useChat.getState().setCitationsOnLast(threadId, cites),
      onDone: () => {
        useChat.getState().finalizeStreamingMessage(threadId);
        useChat.getState().setStreaming(threadId, false);
      },
      onRefused: (_reason, message) => {
        useChat.getState().removeStreamingPlaceholder(threadId);
        useChat.getState().appendMessage(threadId, {
          id: crypto.randomUUID(),
          authorKind: 'prof',
          kind: 'text',
          text: message || 'Per questa domanda non riesco a preparare una spiegazione affidabile.',
        });
        useChat.getState().setStreaming(threadId, false);
      },
      onError: () => {
        useChat.getState().removeStreamingPlaceholder(threadId);
        useChat.getState().appendMessage(threadId, {
          id: crypto.randomUUID(),
          authorKind: 'prof',
          kind: 'text',
          text: 'Si è interrotta la connessione. Prova a tornare al risultato e rientrare nel ripasso.',
        });
        useChat.getState().setStreaming(threadId, false);
      },
    },
  );
}
